import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, Square, Upload, Trash2 } from 'lucide-react';
import { useDAWStore } from '../../store/daw-store';
import * as Tone from 'tone';

// Types for Clip Launcher
export interface ClipSlot {
  id: string;
  trackIndex: number;
  sceneIndex: number;
  name: string;
  audioUrl?: string;
  patternId?: string;
  color: string;
  isPlaying: boolean;
  isQueued: boolean;
  duration: number; // in beats
}

interface ClipLauncherState {
  clips: ClipSlot[];
  playingClips: Set<string>;
  queuedClips: Set<string>;
}

const TRACK_COUNT = 8;
const SCENE_COUNT = 8;

// Color palette for clips
const CLIP_COLORS = [
  '#ef4444', // red
  '#f59e0b', // amber
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

export const ClipLauncher = () => {
  const { bpm, isPlaying } = useDAWStore();
  const [clipState, setClipState] = useState<ClipLauncherState>({
    clips: [],
    playingClips: new Set(),
    queuedClips: new Set(),
  });
  const [selectedSlot, setSelectedSlot] = useState<{ track: number; scene: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioPlayersRef = useRef<Map<string, Tone.Player>>(new Map());

  // Generate clip ID
  const generateClipId = (trackIndex: number, sceneIndex: number) => 
    `clip-${trackIndex}-${sceneIndex}`;

  // Get clip at position
  const getClip = useCallback((trackIndex: number, sceneIndex: number): ClipSlot | undefined => {
    return clipState.clips.find(
      c => c.trackIndex === trackIndex && c.sceneIndex === sceneIndex
    );
  }, [clipState.clips]);

  // Handle file upload for audio clip
  const handleFileUpload = async (trackIndex: number, sceneIndex: number, file: File) => {
    if (!file.type.startsWith('audio/')) {
      alert('Please upload an audio file');
      return;
    }

    // Create object URL for the audio file
    const audioUrl = URL.createObjectURL(file);
    
    // Load audio to get duration
    const audioContext = new AudioContext();
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const durationInBeats = (audioBuffer.duration / 60) * bpm;

    // Create new clip
    const newClip: ClipSlot = {
      id: generateClipId(trackIndex, sceneIndex),
      trackIndex,
      sceneIndex,
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      audioUrl,
      color: CLIP_COLORS[trackIndex % CLIP_COLORS.length],
      isPlaying: false,
      isQueued: false,
      duration: durationInBeats,
    };

    // Create Tone.js player for this clip
    const player = new Tone.Player(audioUrl).toDestination();
    audioPlayersRef.current.set(newClip.id, player);

    setClipState(prev => ({
      ...prev,
      clips: [...prev.clips.filter(c => c.id !== newClip.id), newClip],
    }));
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, trackIndex: number, sceneIndex: number) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleFileUpload(trackIndex, sceneIndex, files[0]);
    }
  };

  // Handle click to upload
  const handleSlotClick = (trackIndex: number, sceneIndex: number) => {
    const clip = getClip(trackIndex, sceneIndex);
    if (!clip) {
      // No clip, open file picker
      setSelectedSlot({ track: trackIndex, scene: sceneIndex });
      fileInputRef.current?.click();
    }
  };

  // Handle file input change
  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedSlot || !e.target.files || e.target.files.length === 0) return;
    
    await handleFileUpload(selectedSlot.track, selectedSlot.scene, e.target.files[0]);
    setSelectedSlot(null);
  };

  // Trigger clip (quantized to next bar)
  const triggerClip = async (trackIndex: number, sceneIndex: number) => {
    const clip = getClip(trackIndex, sceneIndex);
    if (!clip) return;

    const player = audioPlayersRef.current.get(clip.id);
    if (!player) return;

    // Calculate next bar start time
    const transport = Tone.getTransport();
    const currentTime = transport.seconds;
    const beatsPerBar = 4;
    const secondsPerBeat = 60 / bpm;
    const secondsPerBar = beatsPerBar * secondsPerBeat;
    const currentBar = Math.floor(currentTime / secondsPerBar);
    const nextBarTime = (currentBar + 1) * secondsPerBar;

    // Queue clip to start at next bar
    setClipState(prev => ({
      ...prev,
      queuedClips: new Set([...prev.queuedClips, clip.id]),
    }));

    // Schedule playback at next bar
    player.start(`+${nextBarTime - currentTime}`);

    // Update state when playback starts
    setTimeout(() => {
      setClipState(prev => ({
        ...prev,
        playingClips: new Set([...Array.from(prev.playingClips), clip.id]),
        queuedClips: new Set(Array.from(prev.queuedClips).filter(id => id !== clip.id)),
      }));
    }, (nextBarTime - currentTime) * 1000);
  };

  // Stop clip
  const stopClip = (trackIndex: number, sceneIndex: number) => {
    const clip = getClip(trackIndex, sceneIndex);
    if (!clip) return;

    const player = audioPlayersRef.current.get(clip.id);
    if (!player) return;

    player.stop();

    setClipState(prev => ({
      ...prev,
      playingClips: new Set(Array.from(prev.playingClips).filter(id => id !== clip.id)),
      queuedClips: new Set(Array.from(prev.queuedClips).filter(id => id !== clip.id)),
    }));
  };

  // Delete clip
  const deleteClip = (trackIndex: number, sceneIndex: number) => {
    const clip = getClip(trackIndex, sceneIndex);
    if (!clip) return;

    // Stop and dispose player
    const player = audioPlayersRef.current.get(clip.id);
    if (player) {
      player.stop();
      player.dispose();
      audioPlayersRef.current.delete(clip.id);
    }

    // Revoke object URL
    if (clip.audioUrl) {
      URL.revokeObjectURL(clip.audioUrl);
    }

    setClipState(prev => ({
      ...prev,
      clips: prev.clips.filter(c => c.id !== clip.id),
      playingClips: new Set(Array.from(prev.playingClips).filter(id => id !== clip.id)),
      queuedClips: new Set(Array.from(prev.queuedClips).filter(id => id !== clip.id)),
    }));
  };

  // Launch scene (trigger all clips in a row)
  const launchScene = async (sceneIndex: number) => {
    for (let trackIndex = 0; trackIndex < TRACK_COUNT; trackIndex++) {
      const clip = getClip(trackIndex, sceneIndex);
      if (clip && !clipState.playingClips.has(clip.id)) {
        await triggerClip(trackIndex, sceneIndex);
      }
    }
  };

  // Stop all clips in a track
  const stopTrack = (trackIndex: number) => {
    for (let sceneIndex = 0; sceneIndex < SCENE_COUNT; sceneIndex++) {
      const clip = getClip(trackIndex, sceneIndex);
      if (clip && clipState.playingClips.has(clip.id)) {
        stopClip(trackIndex, sceneIndex);
      }
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-white">Clip Launcher</h3>
          <p className="text-sm text-zinc-400 mt-1">
            Grid-based performance mode for triggering loops and samples
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-zinc-400">
            BPM: <span className="text-white font-mono">{bpm}</span>
          </div>
          <div className="text-sm text-zinc-400">
            Transport: <span className={isPlaying ? 'text-green-400' : 'text-zinc-400'}>
              {isPlaying ? 'Playing' : 'Stopped'}
            </span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-white mb-2">How to Use</h4>
        <ul className="text-sm text-zinc-400 space-y-1">
          <li>• Click empty slot or drag audio files to load clips</li>
          <li>• Click clip to trigger playback (quantized to next bar)</li>
          <li>• Click playing clip to stop it</li>
          <li>• Click scene button to launch all clips in that row</li>
          <li>• Click track stop button to stop all clips in that column</li>
        </ul>
      </div>

      {/* Clip Grid */}
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-4">
        <div className="grid gap-2" style={{ gridTemplateColumns: `auto repeat(${TRACK_COUNT}, 1fr)` }}>
          {/* Header Row - Track Labels */}
          <div className="h-8" /> {/* Empty corner */}
          {Array.from({ length: TRACK_COUNT }, (_, trackIndex) => (
            <div key={`track-header-${trackIndex}`} className="flex flex-col items-center gap-1">
              <div className="text-xs font-semibold text-zinc-400">
                Track {trackIndex + 1}
              </div>
              <button
                onClick={() => stopTrack(trackIndex)}
                className="w-full h-6 bg-zinc-800 hover:bg-red-600 border border-zinc-700 rounded text-xs text-zinc-400 hover:text-white transition-colors flex items-center justify-center"
                title="Stop all clips in this track"
              >
                <Square className="w-3 h-3" />
              </button>
            </div>
          ))}

          {/* Grid Rows */}
          {Array.from({ length: SCENE_COUNT }, (_, sceneIndex) => (
            <div key={`scene-${sceneIndex}`} className="contents">
              {/* Scene Label and Launch Button */}
              <div className="flex items-center gap-2">
                <div className="text-xs font-semibold text-zinc-400 w-16">
                  Scene {sceneIndex + 1}
                </div>
                <button
                  onClick={() => launchScene(sceneIndex)}
                  className="h-full px-3 bg-zinc-800 hover:bg-green-600 border border-zinc-700 rounded text-xs text-zinc-400 hover:text-white transition-colors flex items-center justify-center"
                  title="Launch all clips in this scene"
                >
                  <Play className="w-3 h-3" />
                </button>
              </div>

              {/* Clip Slots */}
              {Array.from({ length: TRACK_COUNT }, (_, trackIndex) => {
                const clip = getClip(trackIndex, sceneIndex);
                const isPlaying = clip && clipState.playingClips.has(clip.id);
                const isQueued = clip && clipState.queuedClips.has(clip.id);

                return (
                  <motion.div
                    key={`slot-${trackIndex}-${sceneIndex}`}
                    className={`
                      relative h-20 rounded-lg border-2 transition-all cursor-pointer
                      ${clip ? 'border-zinc-600' : 'border-dashed border-zinc-700'}
                      ${isPlaying ? 'ring-2 ring-green-500' : ''}
                      ${isQueued ? 'ring-2 ring-yellow-500' : ''}
                      ${isDragging ? 'border-blue-500' : ''}
                    `}
                    style={{
                      backgroundColor: clip ? `${clip.color}20` : 'transparent',
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, trackIndex, sceneIndex)}
                    onClick={() => {
                      if (clip) {
                        if (isPlaying) {
                          stopClip(trackIndex, sceneIndex);
                        } else {
                          triggerClip(trackIndex, sceneIndex);
                        }
                      } else {
                        handleSlotClick(trackIndex, sceneIndex);
                      }
                    }}
                  >
                    {clip ? (
                      <>
                        {/* Clip Content */}
                        <div className="absolute inset-0 p-2 flex flex-col justify-between">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-white truncate">
                                {clip.name}
                              </div>
                              <div className="text-xs text-zinc-400 mt-0.5">
                                {clip.duration.toFixed(1)} beats
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteClip(trackIndex, sceneIndex);
                              }}
                              className="p-1 hover:bg-red-600 rounded transition-colors"
                              title="Delete clip"
                            >
                              <Trash2 className="w-3 h-3 text-zinc-400 hover:text-white" />
                            </button>
                          </div>

                          {/* Status Indicator */}
                          <div className="flex items-center gap-1">
                            {isPlaying && (
                              <div className="flex items-center gap-1 text-xs text-green-400">
                                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                                Playing
                              </div>
                            )}
                            {isQueued && (
                              <div className="flex items-center gap-1 text-xs text-yellow-400">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                                Queued
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Color Bar */}
                        <div
                          className="absolute bottom-0 left-0 right-0 h-1 rounded-b-lg"
                          style={{ backgroundColor: clip.color }}
                        />
                      </>
                    ) : (
                      /* Empty Slot */
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Upload className="w-6 h-6 text-zinc-600" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Stats */}
      <div className="flex items-center gap-6 text-sm text-zinc-400">
        <div>
          Total Clips: <span className="text-white font-mono">{clipState.clips.length}</span>
        </div>
        <div>
          Playing: <span className="text-green-400 font-mono">{clipState.playingClips.size}</span>
        </div>
        <div>
          Queued: <span className="text-yellow-400 font-mono">{clipState.queuedClips.size}</span>
        </div>
      </div>
    </div>
  );
};
